if (!customElements.get('sw-facets')) {
  const PRICE_STEPS = 1000;
  const PRICE_CURVE = 3;
  const POPOVER_QUERY = '(min-width: 750px)';
  const SIDEBAR_QUERY = '(min-width: 990px)';

  const parsePrice = (value) => {
    const digits = String(value || '').replace(/[^\d.]/g, '');
    return digits === '' ? null : Math.round(Number(digits));
  };

  const formatPrice = (value) => (value === null || value === '' ? '' : Number(value).toLocaleString('en-US'));

  const nicePrice = (value, max) => {
    if (value <= 0) return 0;
    if (value >= max) return max;
    let step = 5000;
    if (value < 1000) step = 50;
    else if (value < 10000) step = 250;
    else if (value < 50000) step = 500;
    else if (value < 100000) step = 1000;
    return Math.min(max, Math.max(step, Math.round(value / step) * step));
  };

  const priceToPosition = (price, max) => {
    if (!max) return 0;
    const ratio = Math.min(Math.max(price, 0), max) / max;
    return Math.round(Math.pow(ratio, 1 / PRICE_CURVE) * PRICE_STEPS);
  };

  const positionToPrice = (position, max) => {
    if (position >= PRICE_STEPS) return max;
    return nicePrice(Math.pow(position / PRICE_STEPS, PRICE_CURVE) * max, max);
  };

  class SwFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.drawer =
        this.querySelector('[data-sw-drawer]') || document.getElementById(`SwFacetsDrawer-${this.sectionId}`);
      this.sidebar = document.querySelector(`[data-sw-sidebar="${this.sectionId}"]`);
      this.cache = new Map();
      this.openPopKey = null;
      this.initialParams = window.location.search.slice(1);

      [
        'onClick',
        'onChange',
        'onInput',
        'onKeydown',
        'onSubmit',
        'onDocumentPointer',
        'onDocumentClick',
        'onPopState',
        'onScroll',
        'onResize',
        'onPrefetch',
        'placeDrawer',
      ].forEach((name) => (this[name] = this[name].bind(this)));

      // The drawer lives on <body> so it stacks above the sticky header. With a sidebar
      // it sits in the left column on desktop and only moves to <body> below 990px.
      if (this.drawer && this.sidebar) {
        this.sidebarQuery = window.matchMedia(SIDEBAR_QUERY);
        this.sidebarQuery.addEventListener('change', this.placeDrawer);
        this.placeDrawer();
      } else if (this.drawer) {
        document.body.appendChild(this.drawer);
      }

      this.roots().forEach((root) => {
        root.addEventListener('click', this.onClick);
        root.addEventListener('change', this.onChange);
        root.addEventListener('input', this.onInput);
        root.addEventListener('keydown', this.onKeydown);
        root.addEventListener('submit', this.onSubmit);
        root.addEventListener('pointerover', this.onPrefetch);
        root.addEventListener('focusin', this.onPrefetch);
      });
      document.addEventListener('pointerdown', this.onDocumentPointer);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.currentKey = `${window.location.pathname}${window.location.search}`;
      if (this.plpMode) history.replaceState({ ...(history.state || {}), swPlp: this.currentKey }, '');
      else history.replaceState({ ...(history.state || {}), swFacets: this.initialParams }, '');
      this.hydrate();
      this.onScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointer);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.sidebarQuery?.removeEventListener('change', this.placeDrawer);
      document.body.classList.remove('overflow-hidden');
      this.drawer?.remove();
    }

    roots() {
      return [this, this.drawer].filter(Boolean);
    }

    inSidebar() {
      return Boolean(this.sidebar && this.drawer && this.sidebar.contains(this.drawer));
    }

    placeDrawer() {
      const drawer = this.drawer;
      if (this.sidebarQuery.matches) {
        if (!drawer.hidden && drawer.parentElement === document.body) this.closeDrawer({ instant: true });
        this.sidebar.appendChild(drawer);
        drawer.classList.remove('is-open');
        drawer.hidden = false;
        drawer.querySelectorAll('details[data-sw-group]').forEach((group) => (group.open = true));
      } else {
        drawer.hidden = true;
        drawer.classList.remove('is-open');
        document.body.appendChild(drawer);
      }
    }

    get form() {
      return this.drawer?.querySelector('[data-sw-form]');
    }

    canonical(id) {
      return this.drawer?.querySelector(`#${CSS.escape(id)}`);
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const target = event.target;

      const openDrawer = target.closest('[data-sw-open-drawer]');
      if (openDrawer) return this.openDrawer(null, openDrawer);

      if (target.closest('[data-sw-close-drawer]')) return this.closeDrawer();

      if (target.closest('.sw-group__summary') && this.inSidebar()) {
        event.preventDefault();
        return;
      }

      const more = target.closest('[data-sw-show-all]');
      if (more) return this.toggleShowAll(more);

      const pill = target.closest('[data-sw-pop]');
      if (pill) {
        const key = pill.dataset.swPop;
        if (!window.matchMedia(POPOVER_QUERY).matches) return this.openDrawer(key, pill);
        if (this.openPopKey === key) return this.closePop();
        return this.openPop(key, { viaKeyboard: event.detail === 0 });
      }

      if (target.closest('[data-sw-pop-close]')) return this.closePop({ focus: true });

      const preset = target.closest('[data-sw-preset-min], [data-sw-preset-max]');
      if (preset) return this.applyPreset(preset);
    }

    onDocumentClick(event) {
      const link = event.target.closest('a[data-sw-link], a[data-sw-nav], a[data-sw-brand], a[data-sw-model]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (!this.owns(link)) return;
      if (!this.plpMode && !link.hasAttribute('data-sw-link')) return;
      event.preventDefault();
      if (link.getAttribute('aria-disabled') === 'true') return;
      const url = new URL(link.href, window.location.origin);
      this.closePop();

      if (!this.plpMode) return this.render(url.search.slice(1));
      if (link.hasAttribute('data-sw-brand')) return this.go(this.withBrand(this.readState(), link), link);
      if (link.hasAttribute('data-sw-model')) return this.go(this.withModel(this.readState(), link), link);
      if (link.hasAttribute('data-sw-nav')) return this.navigate(`${url.pathname}${url.search}`);

      // Removing one applied filter: keep the brand and model state, take the link's other params.
      const params = new URLSearchParams(url.search);
      params.delete('filter.p.vendor');
      params.delete('page');
      return this.go({ ...this.readState(), params });
    }

    owns(element) {
      return (
        this.roots().some((root) => root.contains(element)) ||
        Boolean(element.closest('#ProductGridContainer'))
      );
    }

    onPrefetch(event) {
      if (!this.plpMode) return;
      const link = event.target.closest?.('a[data-sw-brand], a[data-sw-model], a[data-sw-nav]');
      if (!link || link === this.lastPrefetch) return;
      this.lastPrefetch = link;
      let key;
      if (link.hasAttribute('data-sw-nav')) {
        const url = new URL(link.href, window.location.origin);
        key = `${url.pathname}${url.search}`;
      } else {
        const state = this.readState();
        key = this.targetKey(link.hasAttribute('data-sw-brand') ? this.withBrand(state, link) : this.withModel(state, link));
      }
      if (key !== this.currentKey) this.fetchPage(key).catch(() => {});
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-sw-filter-search]')) return;

      const priceBlock = input.closest('[data-sw-price]');
      if (input.matches('[data-sw-range]')) return this.commitPrice(priceBlock);

      if (input.matches('[data-sw-price-input]')) {
        input.value = formatPrice(parsePrice(input.value));
        this.syncPrice(priceBlock);
      }

      if (input.dataset.swMirror) {
        const canonical = this.canonical(input.dataset.swMirror);
        if (!canonical) return;
        if (input.type === 'checkbox' || input.type === 'radio') canonical.checked = input.checked;
        else canonical.value = input.value;
      } else if (!input.closest('[data-sw-form]')) {
        return;
      }

      if (input.closest('[data-sw-pop-panel="sort"]')) this.closePop({ focus: true });
      this.schedule();
    }

    onInput(event) {
      const input = event.target;

      if (input.matches('[data-sw-filter-search]')) return this.filterOptions(input);
      if (input.matches('[data-sw-range]')) return this.onRangeInput(input);
      if (input.matches('[data-sw-price-input]')) {
        input.value = input.value.replace(/[^\d,]/g, '');
        this.syncPrice(input.closest('[data-sw-price]'));
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.drawerOpen()) {
          event.stopPropagation();
          return this.closeDrawer();
        }
        if (this.openPopKey) {
          event.stopPropagation();
          return this.closePop({ focus: true });
        }
      }

      if (event.key === 'Enter' && event.target.matches('[data-sw-price-input], [data-sw-filter-search]')) {
        event.preventDefault();
        if (event.target.matches('[data-sw-price-input]')) {
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.schedule(0);
    }

    onDocumentPointer(event) {
      if (!this.openPopKey) return;
      const pop = this.querySelector(`[data-sw-pop-panel="${this.openPopKey}"]`);
      const pill = this.querySelector(`[data-sw-pop="${this.openPopKey}"]`);
      if (pop?.contains(event.target) || pill?.contains(event.target)) return;
      this.closePop();
    }

    onPopState(event) {
      if (this.plpMode) {
        const key = `${window.location.pathname}${window.location.search}`;
        if (key === this.currentKey) return;
        this.closePop();
        this.navigate(key, { push: false });
        return;
      }
      const state = event.state;
      if (!state || !('swFacets' in state)) return;
      this.closePop();
      this.render(state.swFacets, { push: false });
    }

    onScroll() {
      const stickyHost = this.closest('.sw-plp__facets') || this;
      const stickyTop = parseFloat(getComputedStyle(stickyHost).top) || 0;
      const stuck = window.scrollY > 0 && stickyHost.getBoundingClientRect().top <= stickyTop + 1;
      this.classList.toggle('is-stuck', stuck);
    }

    onResize() {
      if (this.openPopKey) this.closePop();
    }

    /* ---------- Popovers ---------- */

    openPop(key, { instant = false, viaKeyboard = false } = {}) {
      this.closePop();
      const pill = this.querySelector(`[data-sw-pop="${key}"]`);
      const pop = this.querySelector(`[data-sw-pop-panel="${key}"]`);
      if (!pill || !pop) return;

      pop.hidden = false;
      this.positionPop(pill, pop);
      pill.setAttribute('aria-expanded', 'true');
      pill.classList.add('is-open');
      this.openPopKey = key;

      if (instant) {
        pop.classList.add('is-open', 'is-instant');
      } else {
        requestAnimationFrame(() => pop.classList.add('is-open'));
        const search = pop.querySelector('[data-sw-filter-search]');
        const firstInput = pop.querySelector('input:not([disabled]), button');
        if (search && !viaKeyboard) search.focus({ preventScroll: true });
        else if (viaKeyboard && firstInput) firstInput.focus({ preventScroll: true });
      }
    }

    positionPop(pill, pop) {
      const bar = this.querySelector('.sw-facets__bar');
      const barRect = bar.getBoundingClientRect();
      const pillRect = pill.getBoundingClientRect();
      const width = pop.offsetWidth;
      const alignEnd = pill.classList.contains('sw-pill--sort');
      let left = alignEnd ? pillRect.right - barRect.left - width : pillRect.left - barRect.left;
      left = Math.max(0, Math.min(left, barRect.width - width));
      pop.style.setProperty('--sw-pop-x', `${Math.round(left)}px`);
      pop.style.setProperty('--sw-pop-y', `${Math.round(pillRect.bottom - barRect.top + 8)}px`);
    }

    closePop({ focus = false } = {}) {
      if (!this.openPopKey) return;
      const key = this.openPopKey;
      const pill = this.querySelector(`[data-sw-pop="${key}"]`);
      const pop = this.querySelector(`[data-sw-pop-panel="${key}"]`);
      this.openPopKey = null;
      if (pop) {
        pop.classList.remove('is-open', 'is-instant');
        pop.hidden = true;
      }
      if (pill) {
        pill.setAttribute('aria-expanded', 'false');
        pill.classList.remove('is-open');
        if (focus) pill.focus({ preventScroll: true });
      }
    }

    /* ---------- Drawer ---------- */

    drawerOpen() {
      return Boolean(this.drawer && !this.drawer.hidden && !this.inSidebar());
    }

    openDrawer(groupKey, trigger) {
      if (!this.drawer || this.inSidebar()) return;
      this.closePop();
      this.lastTrigger = trigger || document.activeElement;

      const drawer = this.drawer;
      const sheet = drawer.querySelector('.sw-drawer__sheet');
      drawer.hidden = false;
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => drawer.classList.add('is-open'));

      let focusTarget = drawer.querySelector('.sw-drawer__close');
      const group = groupKey && drawer.querySelector(`[data-sw-group="${groupKey}"]`);
      if (group) {
        if (group.tagName === 'DETAILS') group.open = true;
        focusTarget = group.querySelector('summary') || focusTarget;
        requestAnimationFrame(() => {
          const body = drawer.querySelector('.sw-drawer__body');
          body.scrollTop = group.offsetTop - body.offsetTop - 8;
        });
      }

      if (typeof trapFocus === 'function') trapFocus(sheet, focusTarget);
      else focusTarget?.focus();
    }

    closeDrawer({ instant = false } = {}) {
      const drawer = this.drawer;
      if (!this.drawerOpen()) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (instant) {
        drawer.hidden = true;
      } else {
        window.setTimeout(() => {
          if (!drawer.classList.contains('is-open') && !this.inSidebar()) drawer.hidden = true;
        }, 360);
      }

      if (typeof removeTrapFocus === 'function') removeTrapFocus(instant ? null : this.lastTrigger);
      else if (!instant) this.lastTrigger?.focus();

      if (this.pendingScroll) {
        this.pendingScroll = false;
        this.scrollToResults();
      }
    }

    /* ---------- Option search ---------- */

    filterOptions(input) {
      const scope = input.closest('.sw-group__body, .sw-pop__body');
      if (!scope) return;
      const query = input.value.trim().toLowerCase();
      let visible = 0;
      scope.querySelectorAll('[data-sw-options] > li').forEach((item) => {
        const match = !query || (item.dataset.label || '').includes(query);
        item.hidden = !match;
        if (match) visible += 1;
      });
      const empty = scope.querySelector('[data-sw-options-empty]');
      if (empty) empty.hidden = visible > 0;
      scope.querySelector('[data-sw-options]')?.classList.toggle('is-searching', query !== '');
      const more = scope.querySelector('[data-sw-show-all]');
      if (more) more.hidden = query !== '';
    }

    toggleShowAll(button, expanded = button.getAttribute('aria-expanded') !== 'true') {
      const list = button.parentElement.querySelector('[data-sw-collapsible]');
      if (!list) return;
      list.classList.toggle('is-expanded', expanded);
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    }

    /* ---------- Price ---------- */

    priceParts(block) {
      return {
        max: Number(block.dataset.max) || 0,
        minRange: block.querySelector('[data-sw-range="min"]'),
        maxRange: block.querySelector('[data-sw-range="max"]'),
        minField: block.querySelector('[data-sw-price-input="min"]'),
        maxField: block.querySelector('[data-sw-price-input="max"]'),
        fill: block.querySelector('.sw-price__fill'),
      };
    }

    syncPrice(block) {
      if (!block) return;
      const { max, minRange, maxRange, minField, maxField } = this.priceParts(block);
      const low = parsePrice(minField.value) ?? 0;
      const high = parsePrice(maxField.value) ?? max;
      minRange.value = priceToPosition(Math.min(low, high), max);
      maxRange.value = priceToPosition(Math.max(low, high), max);
      this.paintPrice(block);
    }

    paintPrice(block) {
      const { max, minRange, maxRange, minField, maxField, fill } = this.priceParts(block);
      const start = Number(minRange.value) / PRICE_STEPS;
      const end = Number(maxRange.value) / PRICE_STEPS;
      fill.style.left = `${start * 100}%`;
      fill.style.right = `${(1 - end) * 100}%`;

      const low = parsePrice(minField.value);
      const high = parsePrice(maxField.value);
      block.querySelectorAll('[data-sw-preset-min], [data-sw-preset-max]').forEach((preset) => {
        const presetLow = parsePrice(preset.dataset.swPresetMin);
        const presetHigh = parsePrice(preset.dataset.swPresetMax);
        const pressed = (low || null) === presetLow && (high === null || high >= max ? null : high) === presetHigh;
        preset.setAttribute('aria-pressed', String(pressed && (low !== null || high !== null)));
      });
    }

    onRangeInput(range) {
      const block = range.closest('[data-sw-price]');
      const { max, minRange, maxRange, minField, maxField } = this.priceParts(block);
      const gap = 10;
      if (range === minRange && Number(minRange.value) > Number(maxRange.value) - gap) {
        minRange.value = Math.max(0, Number(maxRange.value) - gap);
      }
      if (range === maxRange && Number(maxRange.value) < Number(minRange.value) + gap) {
        maxRange.value = Math.min(PRICE_STEPS, Number(minRange.value) + gap);
      }
      const low = positionToPrice(Number(minRange.value), max);
      const high = positionToPrice(Number(maxRange.value), max);
      minField.value = Number(minRange.value) <= 0 ? '' : formatPrice(low);
      maxField.value = Number(maxRange.value) >= PRICE_STEPS ? '' : formatPrice(high);
      this.paintPrice(block);
    }

    applyPreset(preset) {
      const block = preset.closest('[data-sw-price]');
      const { minField, maxField } = this.priceParts(block);
      const clear = preset.getAttribute('aria-pressed') === 'true';
      minField.value = clear ? '' : formatPrice(parsePrice(preset.dataset.swPresetMin));
      maxField.value = clear ? '' : formatPrice(parsePrice(preset.dataset.swPresetMax));
      this.syncPrice(block);
      this.commitPrice(block);
    }

    commitPrice(block) {
      const { minField, maxField } = this.priceParts(block);
      [minField, maxField].forEach((field) => {
        if (!field.dataset.swMirror) return;
        const canonical = this.canonical(field.dataset.swMirror);
        if (canonical) canonical.value = field.value;
      });
      this.schedule();
    }

    /* ---------- Rendering ---------- */

    hydrate() {
      this.roots().forEach((root) =>
        root.querySelectorAll('[data-sw-price]').forEach((block) => {
          const { max, minField, maxField } = this.priceParts(block);
          [minField, maxField].forEach((field) => (field.value = formatPrice(parsePrice(field.value))));
          if (!maxField.hasAttribute('data-sw-keep-placeholder')) maxField.placeholder = formatPrice(max);
          this.syncPrice(block);
        }),
      );
    }

    schedule(delay = 200) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        if (this.plpMode) this.go(this.readState());
        else this.render(this.paramsFromForm());
      }, delay);
    }

    paramsFromForm() {
      const form = this.form;
      const params = new URLSearchParams();
      if (!form) return '';

      const priceBlock = form.querySelector('[data-sw-price]');
      const priceMax = priceBlock ? Number(priceBlock.dataset.max) : Infinity;

      Array.from(form.elements).forEach((element) => {
        if (!element.name || element.disabled) return;
        if ((element.type === 'checkbox' || element.type === 'radio') && !element.checked) return;

        let value = element.value;
        if (value === '') return;
        if (element.name === 'sort_by' && value === form.dataset.defaultSort) return;
        // Sort is carried only when chosen; each collection otherwise keeps its own default.
        if (element.name === 'sort_by' && element.defaultChecked && !new URLSearchParams(window.location.search).has('sort_by')) return;

        if (element.dataset.swPriceInput) {
          const amount = parsePrice(value);
          if (amount === null) return;
          if (element.dataset.swPriceInput === 'min' && amount <= 0) return;
          if (element.dataset.swPriceInput === 'max' && amount >= priceMax) return;
          value = String(amount);
        }

        params.append(element.name, value);
      });

      return params.toString();
    }

    /* ---------- Collection routing: brand and model choose the page ---------- */

    get plpMode() {
      return this.dataset.template === 'collection' && Boolean(this.drawer?.querySelector('[data-sw-plp-ctx]'));
    }

    plpContext() {
      const el = this.drawer.querySelector('[data-sw-plp-ctx]');
      let landings = [];
      try {
        landings = JSON.parse(el.dataset.landings || '[]');
      } catch (error) {
        landings = [];
      }
      return {
        mode: el.dataset.mode,
        allPath: el.dataset.allPath,
        pagePath: el.dataset.pagePath,
        collectionsPath: el.dataset.collectionsPath || '/collections',
        modelParam: el.dataset.modelParam || '',
        implicit: el.dataset.implicitName ? { name: el.dataset.implicitName, value: el.dataset.implicitValue } : null,
        landings,
      };
    }

    readState() {
      const brands = [];
      this.drawer
        .querySelectorAll('[data-sw-options] [data-sw-brand].is-selected, .sw-facet-search__chip[data-sw-brand]')
        .forEach((link) => {
          if (brands.some((brand) => brand.vendor === link.dataset.swBrand)) return;
          brands.push({ vendor: link.dataset.swBrand, handle: link.dataset.swHandle || '' });
        });
      const models = [];
      this.drawer
        .querySelectorAll('[data-sw-options] [data-sw-model].is-selected, .sw-facet-search__chip[data-sw-model]')
        .forEach((link) => {
          if (models.some((model) => model.handle === link.dataset.swModel)) return;
          models.push({ handle: link.dataset.swModel, brand: link.dataset.swModelBrand, value: link.dataset.swModelValue || '' });
        });
      return { brands, models, params: new URLSearchParams(this.paramsFromForm()) };
    }

    withBrand(state, link) {
      const vendor = link.dataset.swBrand;
      const selected = state.brands.some((brand) => brand.vendor === vendor);
      if (selected) {
        return {
          ...state,
          brands: state.brands.filter((brand) => brand.vendor !== vendor),
          models: state.models.filter((model) => model.brand !== vendor),
        };
      }
      return { ...state, brands: [...state.brands, { vendor, handle: link.dataset.swHandle || '' }] };
    }

    withModel(state, link) {
      const handle = link.dataset.swModel;
      const brand = link.dataset.swModelBrand;
      if (state.models.some((model) => model.handle === handle)) {
        return { ...state, models: state.models.filter((model) => model.handle !== handle) };
      }
      const model = { handle, brand, value: link.dataset.swModelValue || '' };
      if (this.plpContext().modelParam) return { ...state, models: [...state.models, model] };
      // One model at a time until a model filter exists: its brand becomes the only brand.
      const owner = state.brands.find((entry) => entry.vendor === brand) || { vendor: brand, handle: '' };
      return { ...state, brands: [owner], models: [model] };
    }

    /**
     * Pure routing: brand and model choose the path, every other filter (and sort) rides along.
     * Mirrors snippets/sw-brand-href.liquid, which builds the same URLs for crawlers and no-JS.
     */
    resolveTarget(state, ctx = this.plpContext()) {
      const params = new URLSearchParams(state.params);
      params.delete('filter.p.vendor');
      params.delete('page');
      if (ctx.modelParam) params.delete(ctx.modelParam);

      const brands = state.brands.filter((brand, index, all) => all.findIndex((b) => b.vendor === brand.vendor) === index);
      const models = state.models.filter((model) => brands.some((brand) => brand.vendor === model.brand));
      const refinements = () => [...new Set(params.keys())].filter((key) => key !== 'sort_by');
      const addVendors = () => brands.forEach((brand) => params.append('filter.p.vendor', brand.vendor));
      const addModels = () => {
        if (ctx.modelParam) models.forEach((model) => params.append(ctx.modelParam, model.value));
      };
      const collection = (handle) => `${ctx.collectionsPath}/${handle}`;

      // Style and other cross-brand pages: the page stays, brands and models narrow it...
      if (ctx.mode === 'page') {
        if (!ctx.implicit || refinements().length === 0) {
          addVendors();
          addModels();
          return { path: ctx.pagePath, params };
        }
        // ...unless the page stands for one refinement All Watches can express (Under $10k)
        // and another refinement joins it: then route from All Watches with both.
        if (!params.has(ctx.implicit.name)) params.append(ctx.implicit.name, ctx.implicit.value);
      }

      if (brands.length === 0) {
        const keys = refinements();
        if (keys.length === 1 && params.getAll(keys[0]).length === 1) {
          const landing = ctx.landings.find((entry) => entry.name === keys[0] && String(entry.value) === params.get(keys[0]));
          if (landing) {
            params.delete(keys[0]);
            return { path: landing.path, params };
          }
        }
        return { path: ctx.allPath, params };
      }

      if (brands.length === 1 && brands[0].handle) {
        if (models.length === 1) return { path: collection(models[0].handle), params };
        addModels();
        return { path: collection(brands[0].handle), params };
      }

      addVendors();
      addModels();
      return { path: ctx.allPath, params };
    }

    targetKey(state) {
      const { path, params } = this.resolveTarget(state);
      const query = params.toString();
      return `${path}${query ? `?${query}` : ''}`;
    }

    go(state, link) {
      window.clearTimeout(this.timer);
      if (link) this.markSelection(link);
      this.navigate(this.targetKey(state));
    }

    markSelection(link) {
      const { swBrand: brand, swModel: model } = link.dataset;
      const selector = brand
        ? `[data-sw-options] [data-sw-brand="${CSS.escape(brand)}"]`
        : `[data-sw-options] [data-sw-model="${CSS.escape(model)}"]`;
      const tiles = this.drawer.querySelectorAll(selector);
      const on = !Array.from(tiles).some((tile) => tile.classList.contains('is-selected'));
      tiles.forEach((tile) => tile.classList.toggle('is-selected', on));
      if (!on) {
        const chipSelector = brand ? `[data-sw-brand="${CSS.escape(brand)}"]` : `[data-sw-model="${CSS.escape(model)}"]`;
        this.drawer.querySelectorAll(`.sw-facet-search__chip${chipSelector}`).forEach((chip) => chip.remove());
      }
    }

    fetchPage(key, signal) {
      const cached = this.cache.get(key);
      if (cached) return cached;
      const request = fetch(key, { signal, credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Page request failed: ${response.status}`);
        return response.text();
      });
      request.catch(() => this.cache.delete(key));
      this.cache.set(key, request);
      if (this.cache.size > 40) this.cache.delete(this.cache.keys().next().value);
      return request;
    }

    async navigate(key, { push = true } = {}) {
      if (push && key === this.currentKey) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setLoading(true);
      if (push) history.pushState({ swPlp: key }, '', key);
      const updateEvent = this.startUpdateEvent(key.split('?')[1] || '');

      try {
        const html = await this.fetchPage(key, controller.signal);
        if (controller !== this.controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (!this.swapPage(doc)) throw new Error('Target page uses a different layout');
        this.currentKey = key;
        updateEvent?.resolve(parseInt(this.dataset.count, 10) || 0);

        if (this.drawerOpen()) this.pendingScroll = true;
        else this.scrollToResults();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        updateEvent?.reject(error);
        window.location.assign(key);
      } finally {
        if (controller === this.controller) this.setLoading(false);
      }
    }

    swapPage(doc) {
      const main = document.getElementById('MainContent');
      const nextMain = doc.getElementById('MainContent');
      const gridSection = this.closest('.shopify-section');
      const nextGridSection = gridSection && nextMain?.querySelector(`#${CSS.escape(gridSection.id)}`);
      if (!main || !nextGridSection || !doc.getElementById(this.drawer.id)) return false;

      // Every section but the one holding the filters is replaced wholesale, in the target's order.
      const nextSections = Array.from(nextMain.children);
      const gridIndex = nextSections.indexOf(nextGridSection);
      Array.from(main.children).forEach((element) => {
        if (element !== gridSection) element.remove();
      });
      nextSections.slice(0, gridIndex).forEach((element) => main.insertBefore(this.adopt(element), gridSection));
      let anchor = gridSection;
      nextSections.slice(gridIndex + 1).forEach((element) => {
        const node = this.adopt(element);
        anchor.after(node);
        anchor = node;
      });

      const component = gridSection.querySelector('collection-component');
      const nextComponent = nextGridSection.querySelector('collection-component');
      if (component && nextComponent) {
        ['view-event-payload', 'data-collection-id'].forEach((name) => {
          const value = nextComponent.getAttribute(name);
          if (value !== null) component.setAttribute(name, value);
        });
      }

      this.swap(doc);
      this.swapHead(doc);
      return true;
    }

    adopt(element) {
      const node = document.importNode(element, true);
      node.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
      node.querySelectorAll('script').forEach((script) => {
        const type = script.getAttribute('type');
        if (type && type !== 'text/javascript' && type !== 'module') return;
        const src = script.getAttribute('src');
        if (src && document.querySelector(`script[src="${CSS.escape(src)}"]`)) return;
        const fresh = document.createElement('script');
        Array.from(script.attributes).forEach((attr) => fresh.setAttribute(attr.name, attr.value));
        fresh.textContent = script.textContent;
        script.replaceWith(fresh);
      });
      window.requestAnimationFrame(() => window.SwClamp?.init(node));
      return node;
    }

    swapHead(doc) {
      document.title = doc.title;
      [
        'meta[name="description"]',
        'link[rel="canonical"]',
        'meta[name="robots"]',
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[property="og:url"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
      ].forEach((selector) => {
        const current = document.head.querySelector(selector);
        const next = doc.head.querySelector(selector);
        if (current && next) current.replaceWith(document.importNode(next, true));
        else if (next) document.head.appendChild(document.importNode(next, true));
        else if (current) current.remove();
      });
    }

    async render(params, { push = true } = {}) {
      const url = `${window.location.pathname}?section_id=${this.sectionId}${params ? `&${params}` : ''}`;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setLoading(true);

      const updateEvent = this.startUpdateEvent(params);
      if (push) {
        history.pushState({ swFacets: params }, '', `${window.location.pathname}${params ? `?${params}` : ''}`);
      }

      try {
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
          html = await response.text();
          this.cache.set(url, html);
        }
        if (controller !== this.controller) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.swap(doc);
        updateEvent?.resolve(parseInt(this.dataset.count, 10) || 0);

        if (this.drawerOpen()) this.pendingScroll = true;
        else this.scrollToResults();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        updateEvent?.reject(error);
        window.location.assign(`${window.location.pathname}${params ? `?${params}` : ''}`);
      } finally {
        if (controller === this.controller) this.setLoading(false);
      }
    }

    swap(doc) {
      const grid = document.getElementById('ProductGridContainer');
      const nextGrid = doc.getElementById('ProductGridContainer');
      if (grid && nextGrid) {
        grid.innerHTML = nextGrid.innerHTML;
        grid.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      }

      document.querySelectorAll('[data-sw-facets-sync]').forEach((element) => {
        const next = doc.querySelector(`[data-sw-facets-sync="${element.dataset.swFacetsSync}"]`);
        if (next) element.innerHTML = next.innerHTML;
      });

      const nextRoot = doc.querySelector('sw-facets');
      if (!nextRoot) return;
      this.dataset.count = nextRoot.dataset.count || '';
      this.dataset.collectionId = nextRoot.dataset.collectionId || '';
      this.dataset.collectionHandle = nextRoot.dataset.collectionHandle || '';

      const state = this.captureState();
      const nextDrawer = (this.drawer && doc.getElementById(this.drawer.id)) || nextRoot;
      this.roots().forEach((root) => {
        const source = root === this ? nextRoot : nextDrawer;
        root.querySelectorAll('[data-sw-region]').forEach((region) => {
          const next = source.querySelector(`[data-sw-region="${region.dataset.swRegion}"]`);
          if (next) region.innerHTML = next.innerHTML;
        });
      });
      this.hydrate();
      this.restoreState(state);
    }

    captureState() {
      const drawerBody = this.drawer?.querySelector('.sw-drawer__body');
      const groups = {};
      this.drawer?.querySelectorAll('details[data-sw-group]').forEach((group) => {
        groups[group.dataset.swGroup] = group.open;
      });
      const searches = {};
      this.roots().forEach((root) =>
        root.querySelectorAll('[data-sw-filter-search]').forEach((input) => {
          if (input.value) searches[input.id] = input.value;
        }),
      );
      const expanded = [];
      this.drawer?.querySelectorAll('[data-sw-collapsible].is-expanded').forEach((list) => {
        expanded.push(list.dataset.swCollapsible);
      });
      const active = document.activeElement;
      const pop = this.openPopKey && this.querySelector(`[data-sw-pop-panel="${this.openPopKey}"] .sw-pop__body`);

      return {
        groups,
        searches,
        expanded,
        sideScroll: this.sidebar ? this.sidebar.scrollTop : 0,
        popKey: this.openPopKey,
        popScroll: pop ? pop.scrollTop : 0,
        drawerScroll: drawerBody ? drawerBody.scrollTop : 0,
        quickScroll: this.querySelector('[data-sw-quick]')?.scrollLeft || 0,
        focusId: active && active.id && this.roots().some((root) => root.contains(active)) ? active.id : null,
      };
    }

    restoreState(state) {
      this.drawer?.querySelectorAll('details[data-sw-group]').forEach((group) => {
        const key = group.dataset.swGroup;
        if (key in state.groups) group.open = state.groups[key];
      });

      state.expanded.forEach((key) => {
        const button = this.drawer?.querySelector(`[data-sw-show-all="${CSS.escape(key)}"]`);
        if (button) this.toggleShowAll(button, true);
      });
      if (this.sidebar) this.sidebar.scrollTop = state.sideScroll;

      Object.entries(state.searches).forEach(([id, value]) => {
        const input = document.getElementById(id);
        if (!input) return;
        input.value = value;
        this.filterOptions(input);
      });

      const quick = this.querySelector('[data-sw-quick]');
      if (quick) quick.scrollLeft = state.quickScroll;

      this.openPopKey = null;
      if (state.popKey) {
        this.openPop(state.popKey, { instant: true });
        const popBody = this.querySelector(`[data-sw-pop-panel="${state.popKey}"] .sw-pop__body`);
        if (popBody) popBody.scrollTop = state.popScroll;
      }

      const drawerBody = this.drawer?.querySelector('.sw-drawer__body');
      if (drawerBody) drawerBody.scrollTop = state.drawerScroll;

      const focusTarget = state.focusId && document.getElementById(state.focusId);
      if (focusTarget) {
        if (this.drawerOpen() && typeof trapFocus === 'function') {
          trapFocus(this.drawer.querySelector('.sw-drawer__sheet'), focusTarget);
        } else {
          focusTarget.focus({ preventScroll: true });
        }
      }
    }

    scrollToResults() {
      const host = this.closest('.sw-plp__facets') || this;
      const hostStyle = getComputedStyle(host);
      const stickyTop =
        hostStyle.position === 'sticky'
          ? parseFloat(hostStyle.top) || 0
          : parseFloat(hostStyle.getPropertyValue('--sw-sticky-offset')) || 0;
      const hostTop = host.getBoundingClientRect().top;
      if (hostTop > stickyTop + 1) return;
      const container = host.parentElement || host;
      const target = container.getBoundingClientRect().top + window.scrollY - stickyTop;
      window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      this.drawer?.classList.toggle('is-loading', loading);
      const grid = document.getElementById('ProductGridContainer');
      window.clearTimeout(this.skeletonTimer);
      grid?.classList.toggle('sw-is-loading', loading);
      if (!loading) {
        grid?.classList.remove('sw-is-skeleton');
        return;
      }
      // Cached swaps land before the skeleton would show, so it only appears on slow fetches.
      this.skeletonTimer = window.setTimeout(() => grid?.classList.add('sw-is-skeleton'), 120);
    }

    startUpdateEvent(params) {
      const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
      if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

      const searchParams = new URLSearchParams(params);
      const isSearchPage = this.dataset.template === 'search';
      const isCollectionPage = this.dataset.template === 'collection';
      let deferred;

      if (isSearchPage && SearchUpdateEvent) {
        deferred = SearchUpdateEvent.createPromise();
        this.dispatchEvent(
          new SearchUpdateEvent({
            search: {
              query: searchParams.get('q') || '',
              productFilters: SearchUpdateEvent.parseProductFilters(searchParams),
              sortKey: SearchUpdateEvent.getSortKey(searchParams),
            },
            promise: deferred.promise,
          }),
        );
      } else if (isCollectionPage && CollectionUpdateEvent) {
        deferred = CollectionUpdateEvent.createPromise();
        this.dispatchEvent(
          new CollectionUpdateEvent({
            collection: {
              id: this.dataset.collectionId || null,
              handle: this.dataset.collectionHandle || '',
              productsCount: parseInt(this.dataset.count, 10) || 0,
            },
            productFilters: CollectionUpdateEvent.parseProductFilters(searchParams),
            sortKey: CollectionUpdateEvent.getSortKey(searchParams),
            promise: deferred.promise,
          }),
        );
      }

      if (!deferred) return;
      return {
        resolve: (count) => deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: count }),
        reject: deferred.reject,
      };
    }
  }

  customElements.define('sw-facets', SwFacets);
}
